import { useEffect } from "react";
import { useQueries } from "@tanstack/react-query";
import { ClipboardList } from "lucide-react";
import { Label } from "@/components/ui/label";
import { apiGet } from "@/lib/api";

export interface QuizChoice {
  questionCount: number;
  passScore: number;
  enableQuiz: boolean;
}

/** Clamp to the allowed ranges (call before sending). */
export function normalizeQuizChoice(v: QuizChoice): QuizChoice {
  return {
    ...v,
    questionCount: Math.min(10, Math.max(5, Math.round(v.questionCount) || 5)),
    passScore: Math.min(100, Math.max(65, Math.round(v.passScore) || 65)),
  };
}

export const DEFAULT_QUIZ_CHOICE: QuizChoice = { questionCount: 5, passScore: 65, enableQuiz: false };

interface QuizInfo {
  quiz: { id: string; passScore: number; isActive?: boolean } | null;
  questions: unknown[];
}

/**
 * Quiz options for assigning one or more documents.
 *  - A document whose quiz is active always needs it: the assigner can only
 *    choose how many questions (5-10, default 5) and the passing score (65-100).
 *  - A document whose quiz is switched off but still has a question bank gets
 *    an opt-in for this assignment only.
 *  - Documents without a quiz are not affected.
 * Renders nothing when no selected document has a question bank.
 */
export function QuizAssignmentSettings({
  docs,
  value,
  onChange,
}: {
  docs: { id: string; title: string }[];
  value: QuizChoice;
  onChange: (v: QuizChoice) => void;
}) {
  const results = useQueries({
    queries: docs.map((d) => ({
      queryKey: ["admin", "doc-quiz", d.id],
      queryFn: () => apiGet<QuizInfo>(`/api/admin/safety-docs/${d.id}/quiz`),
      staleTime: 30_000,
    })),
  });

  const withBank = docs
    .map((d, i) => ({ doc: d, info: results[i]?.data }))
    .filter((x) => !!x.info?.quiz && (x.info?.questions.length ?? 0) > 0);
  const active = withBank.filter((x) => x.info!.quiz!.isActive !== false);
  const optional = withBank.filter((x) => x.info!.quiz!.isActive === false);

  // Start from the strictest passing score among the active quizzes.
  const defaultPass = Math.max(65, ...active.map((x) => x.info!.quiz!.passScore));
  const activeKey = active.map((x) => x.doc.id).join(",");
  useEffect(() => {
    if (active.length > 0) onChange({ ...value, passScore: Math.max(value.passScore, defaultPass) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKey, defaultPass]);

  if (withBank.length === 0) return null;
  const showControls = active.length > 0 || value.enableQuiz;

  return (
    <div className="space-y-3 rounded-lg border border-violet-200 dark:border-violet-800 bg-violet-50/50 dark:bg-violet-900/20 p-3">
      <div className="flex items-center gap-2">
        <ClipboardList className="h-4 w-4 text-violet-600" />
        <Label className="text-xs font-semibold uppercase tracking-wide text-violet-700 dark:text-violet-300">Quiz settings</Label>
      </div>

      {active.length > 0 && (
        <p className="text-[11px] text-muted-foreground">
          Quiz required before signing for: <strong>{active.map((x) => x.doc.title).join(", ")}</strong>. It can't be turned off for these documents. Locked after 3 failed attempts.
        </p>
      )}

      {optional.length > 0 && (
        <label className="flex items-start gap-2 text-xs">
          <input
            type="checkbox"
            checked={value.enableQuiz}
            onChange={(e) => onChange({ ...value, enableQuiz: e.target.checked })}
            className="mt-0.5"
          />
          <span>
            Also give a quiz on this assignment for: <strong>{optional.map((x) => x.doc.title).join(", ")}</strong>
            <span className="block text-[11px] text-muted-foreground">These documents have a question bank, but their quiz is switched off.</span>
          </span>
        </label>
      )}

      {showControls && (
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            Questions
            <input
              type="number" min={5} max={10} value={value.questionCount}
              onChange={(e) => onChange({ ...value, questionCount: Number(e.target.value) })}
              onBlur={() => onChange(normalizeQuizChoice(value))}
              className="w-16 h-7 rounded-md border border-border bg-background px-2 text-xs text-center font-medium text-foreground"
            />
            <span className="text-[11px]">(5–10, default 5)</span>
          </label>
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            Passing score %
            <input
              type="number" min={65} max={100} value={value.passScore}
              onChange={(e) => onChange({ ...value, passScore: Number(e.target.value) })}
              onBlur={() => onChange(normalizeQuizChoice(value))}
              className="w-16 h-7 rounded-md border border-border bg-background px-2 text-xs text-center font-medium text-foreground"
            />
            <span className="text-[11px]">(min 65)</span>
          </label>
        </div>
      )}
    </div>
  );
}
